import { useCallback, useState } from 'react';
import { useLocation } from 'react-router';

import { useRegisterComponent } from '../../orchestrator';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../stores/redux.store';
import type { RememberedAccount as RememberedAccountT } from '../../../reducers/login-reducer';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import {
  requestRemoveRememberedAccount,
  resetRememberMeResponseCodes,
} from '../../../actions/remove-remembered-account';
import { RemoveRememberedAccountResponseTypes } from '../../../types';
import { resetRememberMeState, toggleRememberMeCheckbox } from '../../../actions/login-actions';
import { useNavigateSoftOrHard } from '../../../utilities/hooks/useNavigateSoftOrHard';

export type UseRememberMeReturnType = {
  /**
   * Function that's used to redirect the user to the login page with the `prompt=login` query param
   *
   * This is used on the login page where the user has remembered accounts and wants to login with a different account.
   */
  onAddAccountSelected: () => void;
  /**
   * Function that's used to enable the ability to delete remembered accounts.
   *
   * This is used on the login page where the user has remembered accounts and wants to remove an existing account.
   */
  onEnableDeleteMode: () => void;
  /**
   * Indicates whether the "remember me" feature is enabled.
   */
  rememberMeEnabled: boolean;
  /**
   * Array of accounts that the user has chosen to remember for the "remember me" feature.
   */
  rememberedAccounts: RememberedAccountT[];
  /**
   * Function to delete an account from the list of remembered accounts.
   */
  onDeleteAccount: (account: RememberedAccountT) => void;
  /**
   * Function to disable the ability to delete remembered accounts.
   */
  onDisableDeleteMode: () => void;
  /**
   * Indicates whether the user is in the "delete mode" state, where they can remove accounts.
   */
  isDeleteModeEnabled: boolean;
  /**
   * Value of the "remember me" checkbox.
   */
  isRememberMeChecked: boolean;
  /**
   * Function to handle toggling the value of the remember me checkbox.
   */
  onToggleRememberMeCheckbox: () => void;
  /**
   * Messages to display. Are set when the request to get the remembered accounts occurs, whether it's success or failure.
   *
   * Allows us to display messages to the user about the status of the request.
   */
  rememberedAccountsResponseCodes: RemoveRememberedAccountResponseTypes[];
};

/**
 * Hook that provides functionality for the 'remember me' feature.
 */
export const useRememberMe = (): UseRememberMeReturnType => {
  useRegisterComponent(useRememberMe.name);

  const analyticsClient = useAnalyticsClient();

  const dispatch = useAppDispatch();
  const navigate = useNavigateSoftOrHard();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isDeleteModeEnabled, setIsDeleteModeEnabled] = useState(false);

  const rememberMeEnabled = useAppSelector(state => state.login.rememberMeEnabled);
  const rememberedAccounts = useAppSelector(state => state.login.rememberedAccounts);
  const isRememberMeChecked = useAppSelector(state => state.login.isRememberMeChecked);
  const rememberedAccountsResponseCodes = useAppSelector(
    state => state.login.rememberedAccountsResponseCodes
  );

  const onAddAccountSelected = useCallback(() => {
    searchParams.set('prompt', 'login');

    dispatch(resetRememberMeState());

    navigate({
      pathname: '/login',
      search: `?${searchParams.toString()}`,
      hash: location.hash,
    });
  }, [searchParams, dispatch, navigate, location.hash]);

  const onEnableDeleteMode = useCallback(() => {
    setIsDeleteModeEnabled(true);
  }, []);

  const onDeleteAccount = useCallback(
    (account: RememberedAccountT) => {
      // The backend supports deleting multiple accounts at once, so we need to provide accountIds not just the one.
      const accountIds = [account.accountId];

      dispatch(
        requestRemoveRememberedAccount({
          accountIds,
          analyticsClient,
        })
      );
    },
    [analyticsClient, dispatch]
  );

  const onDisableDeleteMode = useCallback(() => {
    dispatch(resetRememberMeResponseCodes());
    setIsDeleteModeEnabled(false);
  }, [dispatch]);

  const onToggleRememberMeCheckbox = useCallback(() => {
    dispatch(toggleRememberMeCheckbox());
  }, [dispatch]);

  return {
    onAddAccountSelected,
    onEnableDeleteMode,
    rememberMeEnabled,
    rememberedAccounts,
    onDeleteAccount,
    onDisableDeleteMode,
    isDeleteModeEnabled,
    isRememberMeChecked,
    onToggleRememberMeCheckbox,
    rememberedAccountsResponseCodes,
  };
};
